import { getInvoices, deleteInvoice as deleteInvoiceApi } from '../services/api';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

interface Invoice {
  id: string;
  number: string;
  invoiceNumber: string; // 新增 invoiceNumber
  date: string;
  issueDate: string; // 新增 issueDate
  dueDate?: string;
  customerName: string;
  customerAddress?: string;
  customerTaxId?: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  taxRate: number;
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  notes?: string;
  description?: string; // 新增 description
  items: InvoiceItem[];
  createdAt: string;
  updatedAt: string;
  archived?: boolean;
}

export const useInvoiceStoreBase = createEntityStore<Invoice>({
  entityName: 'invoice',
  fetchFn: (page: number) => getInvoices(page, 20),
  deleteFn: deleteInvoiceApi,
  toast,
  messages: {
    deleteSuccess: () => i18n.t('common.messages.deleteSuccess'),
    deleteError: () => i18n.t('common.messages.deleteFailed'),
    fetchError: () => i18n.t('common.messages.fetchFailed'),
  },
});

// 封存發票 store - 參考 CRM 的做法，使用 archived 欄位
export const useArchivedInvoiceStoreBase = createEntityStore<Invoice>({
  entityName: 'archivedInvoice',
  fetchFn: (page: number) => getInvoices(page, 20, undefined, undefined, true),
  deleteFn: deleteInvoiceApi,
  toast,
  messages: {
    deleteSuccess: () => i18n.t('common.messages.deleteSuccess'),
    deleteError: () => i18n.t('common.messages.deleteFailed'),
    fetchError: () => i18n.t('common.messages.fetchFailed'),
  },
});

export const useArchivedInvoiceStore = () => {
  const store = useArchivedInvoiceStoreBase();

  return {
    invoices: store.items,
    pagination: store.pagination,
    error: store.error,
    isLoading: store.isLoading,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    isDeleting: store.isDeleting,
    fetchInvoices: store.fetchItems,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete: store.confirmDelete,
    closeDeleteModal: store.closeDeleteModal,
    refreshData: store.refreshData,
  };
};

export const useInvoiceStore = () => {
  const store = useInvoiceStoreBase();

  // 自定義刪除邏輯，刪除後清除報表快取
  const confirmDeleteWithCacheClear = async () => {
    // 刪除前先取得發票年份
    const invoiceToDelete = store.items.find(i => i.id === store.itemToDeleteId);
    const invoiceYear = invoiceToDelete ? new Date(invoiceToDelete.date || invoiceToDelete.issueDate).getFullYear().toString() : undefined;

    await store.confirmDelete();

    // 清除被刪除發票年份的報表快取
    if (invoiceYear) {
      import('./reportStore').then(({ useReportStore }) => {
        const reportStore = useReportStore.getState();
        reportStore.clearYearCache(invoiceYear);
      });
    }
  };

  return {
    invoices: store.items,
    pagination: store.pagination,
    currentPage: store.currentPage,
    error: store.error,
    isLoading: store.isLoading,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    itemToDeleteId: store.itemToDeleteId,
    isDeleting: store.isDeleting,
    fetchInvoices: store.fetchItems,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete: confirmDeleteWithCacheClear,
    closeDeleteModal: store.closeDeleteModal,
    lastOptimisticUpdate: store.lastOptimisticUpdate,
    refreshData: store.refreshData,
    updateInvoice: store.updateItem,
    addInvoice: store.addItem,
  };
};